/**
 * The actions an Action Plan could change to, ranked for the Change action dialog.
 * The AI assessment advises; the planner decides. So the list ranks by score when
 * there is one, says plainly when there is none, and never hides an action the
 * server would accept.
 */
import { ActionOptionView, ActionOptionsView, PlanView } from '../data/plan-management.models';

export interface RankedOption {
  option: ActionOptionView;
  /** The plan's action now: shown for comparison, not offered. */
  current: boolean;
  /** What RBA prescribed for this plan's scope. */
  recommended: boolean;
  /** The highest score among the actions it could change to, when it beats the current action's. */
  best: boolean;
  selectable: boolean;
}

/** Until the server scores options, the asset's action catalog is the list. */
export function optionsFromCatalog(plan: PlanView, actions: ReadonlyMap<string, string>): ActionOptionsView {
  return {
    PlanId: plan.PlanId,
    CurrentActionId: plan.ExactInterventionIdentity,
    Recommendation: null,
    Assessment: null,
    Options: [...actions].map(([id, name]) => ({
      ActionId: id,
      Name: name,
      WorkKind: null,
      Score: null,
      Reason: null,
      Factors: [],
      Eligible: true,
      IneligibleReason: null
    }))
  };
}

/**
 * Scored before unscored, higher score first, then by name; actions the server would
 * refuse go last. The current action keeps its place by score, so the planner sees
 * how it compares.
 */
export function rankOptions(view: ActionOptionsView): RankedOption[] {
  const current = view.CurrentActionId;
  const recommended = view.Recommendation?.ActionId ?? null;
  const selectable = (o: ActionOptionView) => o.Eligible && o.ActionId !== current;

  const bestScore = Math.max(
    -1,
    ...view.Options.filter(o => selectable(o) && o.Score !== null).map(o => o.Score as number)
  );
  // "Best fit" means a better choice than the plan has now; when the current action scores
  // at least as well, no alternative is called that.
  const currentScore = view.Options.find(o => o.ActionId === current)?.Score ?? null;
  const beatsCurrent = currentScore === null || bestScore > currentScore;

  return [...view.Options]
    .sort(
      (a, b) =>
        Number(b.Eligible) - Number(a.Eligible) ||
        (b.Score ?? -1) - (a.Score ?? -1) ||
        a.Name.localeCompare(b.Name)
    )
    .map(option => ({
      option,
      current: option.ActionId === current,
      recommended: option.ActionId === recommended,
      best: bestScore >= 0 && beatsCurrent && selectable(option) && option.Score === bestScore,
      selectable: selectable(option)
    }));
}

/** Matches the name, the work kind or the model's reason. */
export function matchesQuery(ranked: RankedOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const o = ranked.option;
  return [o.Name, o.Reason, o.WorkKind].some(text => text?.toLowerCase().includes(q));
}

/** 0.824 -> 82. */
export function scorePercent(score: number | null): number | null {
  return score === null ? null : Math.round(Math.min(1, Math.max(0, score)) * 100);
}
